/* eslint max-classes-per-file: ["error", 6] */

'use strict';

define('vb/action/builtin/fireDataProviderEventAction',['ojs/ojdataprovider', 'vb/action/action', 'vb/private/log', 'vb/private/types/utils/dataProviderUtils',
  'vb/private/types/dataProviderConstants', 'vb/private/utils', 'vb/private/constants'],
(ojDataProvider, Action, Log, DataUtils, DPConstants, Utils, Constants) => {
  /** @typedef {import('vb/action/action').Outcome} Outcome  */
  /** @template K @typedef {import("ojs/ojdataprovider").ItemMetadata<K>} ItemMetadata */
  /** @template K,D @typedef {import('ojs/ojdataprovider').DataProvider<K, D>} DataProvider<K, D> */

  const logger = Log.getLogger('/vb/private/stateManagement/fireDataProviderEventAction');

  const REFRESH_EVENT = DPConstants.DataProviderEvent.REFRESH;
  const MUTATION_EVENTS = [DPConstants.DataProviderMutationEvent.ADD,
    DPConstants.DataProviderMutationEvent.UPDATE, DPConstants.DataProviderMutationEvent.REMOVE];
  const OJ_REFRESH_EVENT = new ojDataProvider.DataProviderRefreshEvent();

  /**
   * Returns the fresh target for an action-chain variable proxy. The explicit proxy marker lets us distinguish
   * a proxy whose fresh target is undefined from a normal object with no proxy target symbol.
   *
   * @param {*} value value to check
   * @returns {*} proxy target when present, otherwise the original value
   */
  function getActionChainProxyTarget(value) {
    // Check the explicit marker before reading the target symbol so a proxy whose fresh target is undefined
    // stays distinguishable from an ordinary object that is not an action-chain assignment proxy.
    if (Utils.isObjectOrArray(value) && value[Constants.IS_CHAIN_ASSIGNMENT_PROXY] === true) {
      return value[Constants.CHAIN_PROXY_TARGET];
    }
    return value;
  }

  /**
   * Clone a DataProvider mutation payload after materializing action-chain variable proxies. This avoids
   * repeatedly resolving the same proxied value while Utils.cloneObject-style enumeration reads each property.
   *
   * @param {*} source payload to clone
   * @returns {*} cloned payload
   */
  function cloneMutationPayload(source) {
    const unwrappedSource = getActionChainProxyTarget(source);

    if (!Utils.isCloneable(unwrappedSource)) {
      return unwrappedSource;
    }

    const target = Array.isArray(unwrappedSource) ? [] : {};

    // eslint-disable-next-line guard-for-in, no-restricted-syntax
    for (const name in unwrappedSource) {
      const copy = getActionChainProxyTarget(unwrappedSource[name]);

      if (Utils.isCloneable(copy)) {
        target[name] = cloneMutationPayload(copy);
      } else {
        target[name] = copy;
      }
    }

    return target;
  }

  /**
   * Mutation operation payload used by update and delete operations. Implements the
   * oj.IteratingDataProviderAddOperationEventDetail interface.
   * @private
   */
  class DataProviderMutationOperationPayload {
    /**
     * Constructs a new instance of DataProviderMutationOperationPayload.
     *
     * @param {object} metadata - The metadata associated with the mutation operation.
     * @param {*} data - The data being added or updated.
     * @param {Array<number>} indexes - An array of indexes affected by the mutation operation.
     * @param {Array<*>} keys - An array of keys associated with the data being mutated.
     */
    constructor(metadata, data, indexes, keys) {
      this.metadata = metadata;
      this.data = data;
      this.indexes = indexes;
      this.keys = keys;
    }
  }

  /**
   * Add operation payload. Implements the oj.IteratingDataProviderMutationOperationEventDetail
   * @private
   */
  class DataProviderAddOperationPayload extends DataProviderMutationOperationPayload {
    /**
     * Constructs a new instance of DataProviderAddOperationPayload.
     *
     * @param {object} metadata - The metadata associated with the add operation.
     * @param {*} data - The data being added.
     * @param {Array<number>} indexes - An array of indexes affected by the add operation.
     * @param {Array<*>} keys - An array of keys associated with the data being added.
     * @param {Array<*>} [afterKeys] - array of keys that the added data should come after.
     * @param {Array<*>} [addBeforeKeys] - array of keys that the added data should come before.
     */
    constructor(metadata, data, indexes, keys, afterKeys, addBeforeKeys) {
      super(metadata, data, indexes, keys);
      this.afterKeys = afterKeys || [];
      this.addBeforeKeys = addBeforeKeys || [];
    }
  }

  /**
   * Implements the ojDataProvider.DataProviderMutationEventDetail. A single event can be raised
   * that includes multiple operations - add / remove / update and this object represents all
   * operations. Note: The only requirement being that if more than operation is present then the
   * keys have to be disjointed. IOW if you had an add and remove in the same mutation event
   * then the keys must not intersect. This requirement is enforced by JET.
   * @private
   */
  class DataProviderEventDetail {
    /**
     * Constructs a new instance of DataProviderEventDetail.
     *
     * Initializes the object with default values:
     * - `add`: null
     * - `remove`: null
     * - `update`: null
     */
    constructor() {
      this.add = null;
      this.remove = null;
      this.update = null;
    }

    /**
     * Sets the add operation details for this DataProviderEventDetail instance.
     *
     * @param {Object} add - The add operation details.
     * @returns {DataProviderEventDetail} This instance for chaining.
     */
    addEvent(add) {
      this.add = add;
      return this;
    }

    /**
     * Sets the update operation details for this DataProviderEventDetail instance.
     *
     * @param {Object} update - The update operation details.
     * @returns {DataProviderEventDetail} This instance for chaining.
     */
    updateEvent(update) {
      this.update = update;
      return this;
    }

    /**
     * Sets the remove operation details for this DataProviderEventDetail instance.
     *
     * @param {Object} remove - The remove operation details.
     * @returns {DataProviderEventDetail} This instance for chaining.
     */
    removeEvent(remove) {
      this.remove = remove;
      return this;
    }
  }

  /**
   * A class that manages dispatching dataProvider events to iterating data providers like
   * ServiceDataProvider, ArrayDataProvider etc.
   * @private
   */
  class DataProviderEventDispatcher {
    /**
     * Constructs a new DataProviderEventDispatcher instance.
     * @param {*} dataProvider - The data provider object.
     * @throws {TypeError} If dataProvider is not an object.
     */
    constructor(dataProvider) {
      this.dataProvider = dataProvider;
      if (Utils.isExtendedType(dataProvider)) {
        this.extendedType = true;
        // @ts-ignore
        const dpValue = dataProvider.getValue();
        // @ts-ignore
        const idProp = (dataProvider.getIdAttributeProperty && dataProvider.getIdAttributeProperty());
        this.idAttribute = idProp ? dpValue[idProp]
          : dpValue[DPConstants.DataProviderIdAttributeProperty.ID_ATTRIBUTE];
        this.itemsPath = dpValue.itemsPath;
        this.log = logger;
      }
    }

    /**
     * Throws an error when an invalid payload is encountered.
     *
     * @param {string} eventType - The type of event that triggered the error.
     * @param {(string|Error)=} [err] - An error message or Error object.
     * @throws {string} An error string indicating that a valid payload was not provided.
     */
    static throwError(eventType, err) {
      const errStr = err || `${eventType} event was raised but a valid payload was not provided!`;
      logger.error(errStr);
      throw errStr;
    }

    /**
     * Checks if the given event type has a valid payload.
     *
     * @param {string} eventType - The type of event to check.
     * @param {object} payload - The payload associated with the event.
     * @returns {boolean} True if the event has a valid payload, false otherwise.
     */
    static isEventWithValidPayload(eventType, payload) {
      if (MUTATION_EVENTS.indexOf(eventType) >= 0) {
        const hasPayload = payload && Object.keys(payload).length > 0;
        if (hasPayload) {
          if (payload.data || payload.keys || payload.indexes) {
            return true;
          }
        }
      }
      return false;
    }

    /**
     * Assembles keys from data if they are not already provided. If keys cannot be built, an error is thrown.
     *
     * @param {string} et - The type of event that triggered the assembly of keys.
     * @param {array} data - The data from which to assemble keys.
     * @param {Set|Array} [keys] - pre-existing keys. If provided, these will be used instead of assembling
     * keys from data.
     * @returns {Set|Array} The assembled keys.
     * @throws {string} If keys cannot be assembled from data or if the number of keys exceeds the number of data items.
     * @throws {string} If the number of keys exceeds the number of data items for non-remove events.
     * @private
     */
    assembleKeysFromData(et, data, keys) {
      let ks = keys;
      let ksLen = ks && (ks instanceof Set ? ks.size : ks.length);
      if ((!ks || !ksLen) && this.canBuildKeys()) {
        // It is mandatory for keys to be present when data is present.
        this.log.warn('No keys were specified in the event payload for the mutation operation', et,
          '. Attempting to build keys from data.');

        // return an empty key set if data is empty
        if (data && data.length === 0) {
          return new Set();
        }

        ks = this._buildKeys(data);
        ksLen = ks && ks.size;
        if (!ksLen || (ksLen === 0 && data.length > 0)) {
          DataProviderEventDispatcher.throwError(et, `${et} event has no keys set for the data, or `
            + `the keys cannot be determined from the data, ${data}`);
        } else if (ksLen > 0 && (ksLen > data.length)) {
          // if keys is present and data size does not match or exceed keys (which is a Set)
          // for non-remove events, throw an error
          if (et !== DPConstants.DataProviderMutationEvent.REMOVE) {
            DataProviderEventDispatcher.throwError(et, `${et} event has more keys ${keys} than the `
              + `data ${data}`);
          }
        }
      }
      return ks && Array.isArray(ks) ? new Set(ks) : ks;
    }

    /**
     * Dispatches a DataProvider event. It can be a refresh event or a mutation event.
     *
     * @param {object} event - the keys are the supported events - refresh, add, remove, update.
     * The value for the event is as follows:
     * - For 'refresh' there is no payload necessary. it's of type oj.DataProviderRefreshEvent
     * - For 'add' operation it's of type oj.DataProviderAddOperationEventDetail
     * - For 'remove' and 'update' operations it's oj.DataProviderOperationEventDetail
     *
     * The payload for a mutation event can contain at least the following properties. Also this
     * action payload specifies as one atomic event, all the mutation operations which occurred.
     * The keys for each operation must be disjoint from each other, e.g. for example an add and
     * remove cannot occur on the same item. In addition, any indexes specified must be
     * monotonically increasing:
     *  - data: optional Array<Object>; the results of the 'add' operation. Note there can be more
     *      than one rows added.
     *  - addBeforeKeys: optional Array<*> (new in JET 7.0) of keys for items located after the
     *      items involved in the add operation. If null and index not specified then insert at
     *      the end.
     *      http://jet.us.oracle.com/jsdocs/oj.DataProviderAddOperationEventDetail.html#addBeforeKeys
     *  - afterKeys: optional Set<*>; (deprecated in JET 7.0) a Set that is the keys of items
     *      located after the items involved in the operation. If null and index not
     *      specified then insert at the end.
     *  - keys: Set<*>. Since SDP variable is configured with idAttribute this can be
     *      determined by SDP itself from the data
     *  - metadata: optional Array<ItemMetadata<Object>>. Since the SDP variable is configured
     *      with 'idAttribute', this can be determined by SDP itself.
     *  - indexes: optional Array<number>
     *
     * @since JET 7.0.x
     * @public
     */
    fireEvent(event) {
      let ojEvent;

      if (Object.keys(event).indexOf(REFRESH_EVENT) >= 0) {
        ojEvent = OJ_REFRESH_EVENT;
      } else {
        let data;
        let metadata;
        let indexes;
        let keys;
        let afterKeys;
        let addBeforeKeys;

        const mutationEventDetail = new DataProviderEventDetail();
        Object.keys(event).forEach((eventType) => {
          const payload = event[eventType];
          // sanity check
          if (!DataProviderEventDispatcher.isEventWithValidPayload(eventType, payload)) {
            DataProviderEventDispatcher.throwError(eventType);
          }

          if (this.extendedType) {
            switch (eventType) {
              case DPConstants.DataProviderMutationEvent.ADD: {
                data = this._getDataItemsArray(payload.data);
                keys = this.assembleKeysFromData(eventType, data, payload.keys);
                ({ metadata } = payload);
                ({ indexes } = payload);
                ({ afterKeys } = payload);
                ({ addBeforeKeys } = payload);

                const afterKeyLen = afterKeys
                  && (afterKeys instanceof Set ? afterKeys.size : afterKeys.length);
                if (afterKeyLen > 0) {
                  this.log.warn('afterKeys parameter is being deprecated on the'
                                  + ' FireDataProviderEventAction. Use addBeforeKeys instead.');
                }

                const operationEventDetail = new DataProviderAddOperationPayload(metadata,
                  // @ts-ignore
                  data, indexes, keys, afterKeys, addBeforeKeys);
                mutationEventDetail.addEvent(operationEventDetail);

                break;
              }
              case DPConstants.DataProviderMutationEvent.UPDATE:
              case DPConstants.DataProviderMutationEvent.REMOVE: {
                data = this._getDataItemsArray(payload.data);
                keys = this.assembleKeysFromData(eventType, data, payload.keys);
                ({ indexes } = payload);
                ({ metadata } = payload);

                const operationEventDetail = new DataProviderMutationOperationPayload(
                  // @ts-ignore
                  metadata, data, indexes, keys);
                if (eventType === DPConstants.DataProviderMutationEvent.UPDATE) {
                  mutationEventDetail.updateEvent(operationEventDetail);
                } else {
                  mutationEventDetail.removeEvent(operationEventDetail);
                }

                break;
              }
              default: {
                const err = `The event ${eventType} is not recognized as a valid DataProvider event`;
                DataProviderEventDispatcher.throwError(eventType, err);
                break;
              }
            }
          } else {
            // for instance types payload is passed-through
            ({ keys } = payload);
            ({ data } = payload);
            ({ metadata } = payload);
            ({ indexes } = payload);
            ({ afterKeys } = payload);
            ({ addBeforeKeys } = payload);
            // keys must be a Set
            keys = keys && Array.isArray(keys) ? new Set(keys) : keys;
            switch (eventType) {
              case DPConstants.DataProviderMutationEvent.ADD: {
                const operationEventDetail = new
                DataProviderAddOperationPayload(metadata, data, indexes, keys, afterKeys, addBeforeKeys);
                mutationEventDetail.addEvent(operationEventDetail);
                break;
              }
              case DPConstants.DataProviderMutationEvent.UPDATE:
              case DPConstants.DataProviderMutationEvent.REMOVE: {
                const operationEventDetail = new
                DataProviderMutationOperationPayload(metadata, data, indexes, keys);
                if (eventType === DPConstants.DataProviderMutationEvent.UPDATE) {
                  mutationEventDetail.updateEvent(operationEventDetail);
                } else {
                  mutationEventDetail.removeEvent(operationEventDetail);
                }
                break;
              }
              default: {
                const err = `The event ${eventType} is not recognized as a valid DataProvider event`;
                DataProviderEventDispatcher.throwError(eventType, err);
                break;
              }
            }
          }
        });

        ojEvent = new ojDataProvider.DataProviderMutationEvent(mutationEventDetail);
      }

      if (ojEvent) {
        // JET throws when something fails processing the dispatched event. VB returns false when there
        try {
          // @ts-ignore
          this.dataProvider.dispatchEvent(ojEvent, true);
          // eslint-disable-next-line no-unused-vars
        } catch (e) {
          const err = `failure occurred when processing dispatched event,
            '${JSON.stringify(event, Utils.setToJSONReplacer, 2)}'.`;
          DataProviderEventDispatcher.throwError(err);
        }
      }
    }

    /**
     * Returns the collection of items in the response data that is located at itemsPath.
     *
     * @param {object|array} jsonData - The response returned from a REST call.
     * @returns {array} The collection of items in the response data.
     * @private
     */
    _getDataItemsArray(jsonData) {
      // TODO: assumes application/json content type
      const { itemsPath } = this;
      let data = jsonData;

      if (itemsPath && (itemsPath.length > 0)) {
        const path = itemsPath.split('.');
        for (let i = 0; i < path.length; i += 1) {
          data = data && data[path[i]];
        }
      }
      // always return a collection because data is
      if (!data || !Array.isArray(data)) {
        data = data ? [data] : [];
      }
      return data;
    }

    /**
     * Returns a set of keys assembled from the provided data using the idAttribute. This method is called only
     * when the keys need to be assembled from data, using the idAttribute. if an idAttribute was not set or is
     * set to '@index' there is no reason to build a keys
     *
     * @param {Array} items - The data from which to assemble keys.
     * @returns {Set} A set of keys or undefined if idAttribute is not set or set to '@index'.
     * @private
     */
    _buildKeys(items) {
      let keySet;
      if (this.idAttribute) {
        const idHelper = DataUtils.getIdAttributeHelper(this.idAttribute);
        // getKeys returns null if idAttribute is set to '@index'
        keySet = items && idHelper.getKeys(items);
      }
      return keySet;
    }

    /**
     * Determines whether keys can be built based on the current configuration.
     *
     * Keys can be built if either:
     * - No ID attribute is defined (`this.idAttribute` is falsy).
     * - The ID attribute is not set to `'@index'`.
     *
     * @returns {boolean} Whether keys can be built.
     * @private
     */
    canBuildKeys() {
      return (!this.idAttribute
      || (this.idAttribute !== DPConstants.DataProviderIdAttribute.AT_INDEX));
    }
  }

  /**
   * whether the dataProvider supports an iteration capability
   * @private
   * @param {DataProvider<K, D>} dataProvider
   * @returns {boolean}
   *
   * @template K type of key
   * @template D type of data
   */
  const isIteratingDataProvider = (dataProvider) => !!(dataProvider.fetchFirst
     && dataProvider.getCapability);

  /**
   * Dispatches an event on a data provider of type oj.DataProvider.
   * @returns {Object} success outcome when event was processed successfully. failure
   * otherwise with the error. The Object has 2 properties:
   * - name: {string} of the outcome. example 'success' or 'failure'
   * - result: error string if failure outcome. undefined otherwise
   *
   * @extends Action
   * @category Action
   */
  class FireDataProviderEventAction extends Action {
    /**
     * Constructs a new instance of FireDataProviderEventAction.
     *
     * @param {string} id - Unique identifier for the action.
     * @param {string} [label] - Label for the action.
     * @inheritdoc
     */
    constructor(id, label) {
      super(id, label);
      this.log = logger;
    }

    /**
     * Dispatches an event to the data provider (of type oj.DataProvider) specified via the 'target' parameter to
     * notify any listeners registered on that data provider to react to changes to the underlying data / state.
     *
     * This method expects either a mutation event or a refresh event as part of the input parameters, but not both.
     *
     * A refresh event is used when the data provider identified by the target needs to be refreshed (possibly
     * forcing a re-fetch of data). A null value is specified because the refresh event does not require a payload.
     *
     * A mutate event is used with VB data providers like ArrayDataProvider2 to update its local (data) cache. A
     * mutation event can include one or more mutation operations like add, update, remove, as long as the
     * id...values between operations do not intersect. For example, you cannot add a record and remove it in the
     * same event.
     *
     * Note: Sub properties on specific operation types are generally based on JET DataProviderOperationEventDetail,
     * DataProviderAddOperationEventDetail and DataProviderRefreshEventDetail. Not all properties on the JET API
     * are supported on this action. Additionally while all sub properties are marked optional, when no value is
     * provided for add/update/remove payload, the action throws a failure outcome.
     *
     * @param {Object} parameters
     * @param {DataProvider<K,D>} parameters.target the target data provider for the event. Usually a VB data provider
     * @param {DataProvider<K,D>} [parameters.dataProvider] deprecated. Use target instead.
     * @param {Object} [parameters.add] mutation operation for adding data to a data provider containing the following
     * properties that are similar to JET DataProviderAddOperationEventDetail.

     * @param {Set.<K>|Array.<K>} [parameters.add.keys] Unique keys of items involved in the add operation, where K is
     * the type of key data
     * @param {Array<K>} [parameters.add.addBeforeKeys] array of keys that the added data should come after.
     * @param {Array<D>} [parameters.add.data] data being added.
     * @param {number[]} [parameters.add.indexes] array of indexes affected by the add operation.
     * @param {Array<ItemMetadata<K>>} [parameters.add.metadata] metadata associated with the mutation operation.
     *
     * @param {Object} [parameters.update] mutation operation for updating data to a data provider containing the
     * following properties that are similar to JET DataProviderOperationEventDetail.
     * @param {Set<K>|Array<K>} [parameters.update.keys] Unique keys of items involved in the mutate operation, where
     * K is the type of key data
     * @param {D[]} [parameters.update.data] data being added.
     * @param {number[]} [parameters.update.indexes] array of indexes affected by the mutate operation.
     * @param {Array<ItemMetadata<K>>} [parameters.update.metadata] metadata associated with the mutation operation.
     *
     *  @param {Object} [parameters.remove] mutation operation for removing data from a data provider containing the
     * following properties that are similar to JET DataProviderOperationEventDetail.
     * @param {Set<K>|Array<K>} [parameters.remove.keys] Unique keys of items involved in the mutate operation, where
     * K is the type of key data
     * @param {Array<D>} [parameters.remove.data] data being added.
     * @param {number[]} [parameters.remove.indexes] array of indexes affected by the mutate operation.
     * @param {Array<ItemMetadata<K>>} [parameters.remove.metadata] metadata associated with the mutation operation.
     *
     * @param {?{}} [parameters.refresh] refresh event does not require a payload, so set null or an empty object.
     * Sub-properties specified for 'refresh' are not passed-through to the data provider.
     *
     * @throws {Error} If both mutation and refresh events are provided, or neither is provided.
     * @throws {Error} If a valid DataProvider instance is not found.
     *
     * @returns {Outcome} An object containing the outcome of the operation.
     *                  The object has two properties:
     *                  - name: {string} The name of the outcome ('success' or 'failure').
     *                  - result: {string|undefined} The error message if the outcome is 'failure', otherwise undefined.
     *
     * @example
     *  A chain that fires a mutation event on an array data provider, by removing an item, add a new item, and
     *  updates another item identified by the key 'prd-1':
     *
     *   const { $page } = context;
     *   await Actions.fireDataProviderEvent(context, {
     *     target: $page.variables.productsADP,
     *     remove: {
     *       keys: [ removedProduct.id ],
     *     },
     *
     *     add: {
     *       data: [newProduct],
     *     },
     *
     *     update: {
     *       data: [ updatedProduct ],
     *       keys: ['prd-1']
     *     },
     *   });
     *
     *  A JSON chain containing an action that dispatches an event to refresh an SDP variable:
     *
     *  {
     *    "actions": {
     *       "fireEventOnDataProvider": {
     *         "module": "vb/action/builtin/fireDataProviderEventAction",
     *         "parameters": {
     *           "target": "{{ $fragment.variables.incidentsSDP }}",
     *           "refresh": null
     *         }
     *       }
     *     }
     *  }
     *
     * @template K type of key
     * @template D type of data
     */
    perform(parameters) {
      const dataProvider = parameters.target || parameters.dataProvider;
      let mutationEvent;
      let refreshEvent;

      MUTATION_EVENTS.forEach((eventName) => {
        if (parameters[eventName]) {
          mutationEvent = mutationEvent || {};
          mutationEvent[eventName] = cloneMutationPayload(parameters[eventName]);
        }
      });

      if (Object.keys(parameters).indexOf(REFRESH_EVENT) >= 0) {
        this.log.finer('payload for', REFRESH_EVENT, 'is ignored, as no payload is required');
        refreshEvent = refreshEvent || {};
        refreshEvent[REFRESH_EVENT] = null;
      }

      if (mutationEvent && refreshEvent) {
        throw new Error(`refresh event cannot be raised along with mutation event. ${parameters}.`);
      } else if (!mutationEvent && !refreshEvent) {
        throw new Error(`either refresh or mutation event needs to be present, not both. ${parameters}.`);
      }

      // we have at least one event.
      const event = refreshEvent || mutationEvent;
      if (this.log.isInfo) {
        if (refreshEvent) {
          this.log.info('FireDataProviderEventAction called with a refresh event');
        } else {
          this.log.info('FireDataProviderEventAction called with a mutation event for operations:',
            JSON.stringify(Object.keys(event)),
            'and payloads:',
            JSON.stringify(Object.values(event)),
            'respectively.');
        }
      }

      // In minified JET this oj class is not exposed.
      // if (oj.DataProviderFeatureChecker.isIteratingDataProvider())
      if (dataProvider && isIteratingDataProvider(dataProvider)) {
        try {
          const eventDipatcher = new DataProviderEventDispatcher(dataProvider);
          eventDipatcher.fireEvent(event);
          return Action.createSuccessOutcome();
        } catch (e) {
          return Action.createFailureOutcome(`Dispatching data provider event ${event} failed`, e);
        }
      } else {
        throw new Error('a valid DataProvider instance is required in order to dispatch the event');
      }
    }
  }

  return FireDataProviderEventAction;
});

